import axios from 'axios';
import {
	TimeSeriesRawDataLatest,
	TimeSeriesRawDataReadings,
	TimeSeriesRawDataTotal,
	TimeSeriesTotalPowerConsumption,
	TimeSeriesDailyPowerConsumption,
	TimeSeriesPast30DaysPowerConsumption,
	TimeSeriesLastYearMonthlyTotalPowerConsumption,
} from '../interfaces/api/timeseries';

export type TimeSeriesSteps = '5m' | '10m' | '15m' | '30m' | '1h' | '1d' | '7d' | '1M';

export type TimeSeriesValueType = 'avg' | 'sum' | 'count' | 'min' | 'max';

export interface TimeSeriesRawDataParam {
	tags: string[];
	start?: string;
	end?: string;
	step?: TimeSeriesSteps;
	type?: TimeSeriesValueType;
	latest?: boolean;
}

export interface TimeSeriesTotalPowerConsumptionParam {
	tag_id: string;
	start_date: string;
	number_of_days: string;
}

export interface TimeSeriesDailyPowerConsumptionParam {
	tag_id: string;
	start_date: string;
	number_of_days: string;
}

export interface TimeSeriesPast30DaysPowerConsumptionParam {
	tags: string[];
	current_date: string;
}

export interface TimeSeriesLastYearMonthlyTotalPowerConsumptionParam {
	tags: string[];
}

function queryBuilderRawData(params: TimeSeriesRawDataParam): URLSearchParams {
	const queryParams = new URLSearchParams();
	if (params.tags && params.tags.length > 0) {
		for (const tag of params.tags) {
			queryParams.append('tags', tag);
		}
	}
	if (params.start) {
		queryParams.append('start', params.start);
	}
	if (params.end) {
		queryParams.append('end', params.end);
	}
	if (params.step) {
		queryParams.append('step', params.step);
	}
	if (params.latest) {
		queryParams.append('latest', String(params.latest));
	}
	if (params.type) {
		queryParams.append('type', params.type);
	}
	return queryParams;
}

export async function getRawDataLatest(
	params: TimeSeriesRawDataParam
): Promise<TimeSeriesRawDataLatest[]> {
	try {
		const queryParams = queryBuilderRawData(params);
		const response = await axios.get<TimeSeriesRawDataLatest[]>(
			'api/timeseries/get_raw_data_latest/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}

export async function getRawDataReadings(
	params: TimeSeriesRawDataParam
): Promise<TimeSeriesRawDataReadings[]> {
	try {
		const queryParams = queryBuilderRawData(params);
		const response = await axios.get<TimeSeriesRawDataReadings[]>(
			'api/timeseries/get_raw_data_readings/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}

export async function getRawDataTotal(
	params: TimeSeriesRawDataParam
): Promise<TimeSeriesRawDataTotal[]> {
	try {
		const queryParams = queryBuilderRawData(params);
		const response = await axios.get<TimeSeriesRawDataTotal[]>(
			'api/timeseries/get_raw_data_total/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}

export async function getTotalPowerConsumption(
	params: TimeSeriesTotalPowerConsumptionParam
): Promise<TimeSeriesTotalPowerConsumption> {
	try {
		const queryParams = new URLSearchParams();
		queryParams.append('tag_id', params.tag_id);
		queryParams.append('start_date', params.start_date);
		queryParams.append('number_of_days', String(params.number_of_days));
		const response = await axios.get<TimeSeriesTotalPowerConsumption>(
			'api/timeseries/get_total_power_consumption/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}

export async function getDailyPowerConsumption(
	params: TimeSeriesDailyPowerConsumptionParam
): Promise<TimeSeriesDailyPowerConsumption> {
	try {
		const queryParams = new URLSearchParams();
		queryParams.append('tag_id', params.tag_id);
		queryParams.append('start_date', params.start_date);
		queryParams.append('number_of_days', String(params.number_of_days));
		const response = await axios.get<TimeSeriesDailyPowerConsumption>(
			'api/timeseries/get_daily_power_consumption/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}

export async function getPast30DaysPowerConsumption(
	params: TimeSeriesPast30DaysPowerConsumptionParam
): Promise<TimeSeriesPast30DaysPowerConsumption> {
	try {
		const queryParams = new URLSearchParams();
		if (params.tags && params.tags.length > 0) {
			for (const tag of params.tags) {
				queryParams.append('tags', tag);
			}
		}
		queryParams.append('current_date', params.current_date);

		const response = await axios.get<TimeSeriesPast30DaysPowerConsumption>(
			'api/timeseries/get_past_30_days_power_consumption/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}

export async function getLastYearMonthlyPowerConsumption(
	params: TimeSeriesLastYearMonthlyTotalPowerConsumptionParam
): Promise<TimeSeriesLastYearMonthlyTotalPowerConsumption> {
	try {
		const queryParams = new URLSearchParams();
		if (params.tags && params.tags.length > 0) {
			for (const tag of params.tags) {
				queryParams.append('tags', tag);
			}
		}

		const response = await axios.get<TimeSeriesLastYearMonthlyTotalPowerConsumption>(
			'api/timeseries/get_last_year_monthly_consumption/',
			{
				params: queryParams,
			}
		);
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			throw error.response;
		}
		throw error;
	}
}
